import { call, put, select, takeLatest } from "redux-saga/effects";
import get from "lodash/get";
import { message, notification } from "antd";
// import { push } from "connected-react-router";
import FarmActions, { FarmTypes, FarmSelectors } from "reducers/FarmRedux";
import getSoilIndexByState from "themes/cashrent/components/map-common/SoilData/getSoilIndexByState";
import getStateAndCounty from "themes/cashrent/components/map-common/utils/getStateAndCounty";
import loadCLUMapData from "themes/cashrent/components/map-common/utils/loadCLUMapData";
import Request from "../common/network/http/Request";
import endpoints from "configs/endpoints";
import { history } from "store";
import { fetchStateByName, fetchCityByNameAndState } from "infrastructure/services/AddressesService";
import { CreateFarmInput } from "schemas/SharedSchemas";

function* handleSoilDataRequest(action) {
  const { callback } = action;
  const farm = yield select(FarmSelectors.farm);

  const timeoutId = setTimeout(() => {
    message.info("Selected property data is taking longer than usual to load, but will be available shortly.", 5);
  }, 15000);

  try {
    const location = yield call(getStateAndCounty, farm.aerialMap);

    const soilData = yield call(
      getSoilIndexByState,
      location.state,
      farm.soilMap || farm.aerialMap,
    );

    yield put(FarmActions.requestSoilDataSuccess({
      soilData,
      location,
    }));

    callback(); // TODO: remove this last resort kinda thing,
    // this is just because the connected router action doesn't seem to work -.-
  } catch (err) {
    notification.error({
      message: "Something went wrong!",
      description: "We're unable to retrieve soil data. Please try again later.",
    });
    // eslint-disable-next-line no-console
    console.error(`[soilDataRequest] err: ${err.message}`);
    yield put(FarmActions.requestSoilDataFailure());
  } finally {
    clearTimeout(timeoutId);
  }
}

function* handleFetchParcelCLUs(action) {
  const { callback } = action;
  const farm = yield select(FarmSelectors.farm);

  const timeoutId = setTimeout(() => {
    message.info("Selected property data is taking longer than usual to load, but will be available shortly.", 5);
  }, 15000);

  try {
    const cluFeatures = yield call(
      loadCLUMapData,
      farm.aerialMap,
    );

    yield put(FarmActions.fetchParcelCLUCompleted({
      cluFeatures,
    }));
  } catch (err) {
    message.info("This area doesn’t have CLU information. You can proceed with the drawing tool");
    // eslint-disable-next-line no-console
    console.error(`[loadCLUMapData] err: ${err.message}`);
    yield put(FarmActions.fetchParcelCLUCompleted());
  } finally {
    clearTimeout(timeoutId);
    // this is just because the connected router action doesn't seem to work -.-
    callback(); // TODO: remove this last resort kinda thing,
  }
}

function* handleUpdateLocation(action) {
  const { location } = action.data;

  const state = yield call(fetchStateByName, location.state);
  const city = yield call(fetchCityByNameAndState, location.city, state.id, location.address);

  yield put(FarmActions.setFarmLocation({
    cityId: city.id,
    stateId: state.id,
  }));
}

function* handleUpdateFarmAddress(action) {
  const { id, data, callback } = action;

  const input = {
    cityId: data.cityId,
    zipCode: data.zipCode,
    address: data.address,
    lat: data.lat,
    lng: data.lng,
    listingId: data.listingId,
  };

  try {
    const result = yield call(Request.put.bind(Request), endpoints.farm.update(id), input);

    if (!get(result, "data.error")) {
      yield put(FarmActions.updateFarmAddressSuccess(data));
      callback();
    } else {
      yield put(FarmActions.updateFarmAddressFailure());
    }
  } catch (err) {
    yield put(FarmActions.updateFarmAddressFailure());
  }
}

// const toGeoJSONInput = (geoJSON) => {
//   if (!get(geoJSON, "data", null)) return;
//   return geoJSON;
// };
// aerialMapData: toGeoJSONInput(state.farm.aerialMapData),
// soilMapData: toGeoJSONInput(state.farm.soilMapData),

function* handleCreateFarm(action) {
  const { data } = action;
  const farm = yield select(FarmSelectors.farm);
  farm.location.zipCode = data.zipCode;
  const farmClean = CreateFarmInput.clean(farm);
  const input = {
    ...farmClean,
    cityId: data.cityId,
  };

  try {
    const result = yield call(Request.post.bind(Request), endpoints.farm.create, input);

    if (!get(result, "data.error")) {
      yield call(history.push, "/my-account/farms");
      yield put(FarmActions.createFarmSuccess());
    } else {
      yield put(FarmActions.createFarmFailure());
    }
  } catch (err) {
    yield put(FarmActions.createFarmFailure());
  }
}

function* handleFetchFarm(action) {
  const { id } = action;
  try {
    const result = yield call(Request.get.bind(Request), endpoints.farm.get(id));

    if (!get(result, "data.error")) {
      yield put(FarmActions.fetchFarmSuccess(result));
    } else {
      yield put(FarmActions.fetchFarmFailure());
    }
  } catch (err) {
    yield put(FarmActions.fetchFarmFailure());
  }
}

function* handleUpdateFarm(action) {
  const { id, data, callback } = action;

  const farm = yield select(FarmSelectors.farm);
  const landValues = yield select(FarmSelectors.landValues);

  const input = {
    cityId: data.cityId,
    zipCode: data.zipCode,
    address: data.address,
    listingId: data.listingId,
    aerialMap: farm.aerialMap,
    name: data.name,
  };

  if (!data.isHunting) {
    input.soilMap = farm.soilMap;
    input.soilIndex = landValues.soilIndex;
  }

  try {
    const result = yield call(Request.put.bind(Request), endpoints.farm.update(id), input);

    if (!get(result, "data.error")) {
      yield put(FarmActions.updateFarmSuccess(data));
      callback();
    } else {
      yield put(FarmActions.updateFarmFailure());
    }
  } catch (err) {
    yield put(FarmActions.updateFarmFailure());
  }
}

function* handleFetchFarmOverview(action) {
  const { id } = action;
  try {
    const result = yield call(Request.get.bind(Request), endpoints.farm.overview(id));

    if (!get(result, "data.error")) {
      yield put(FarmActions.fetchFarmOverviewSuccess(result));
    } else {
      yield put(FarmActions.fetchFarmOverviewFailure());
    }
  } catch (err) {
    yield put(FarmActions.fetchFarmOverviewFailure());
  }
}

export default function* () {
  return [
    yield takeLatest(FarmTypes.FETCH_FARM, handleFetchFarm),
    yield takeLatest(FarmTypes.REQUEST_SOIL_DATA, handleSoilDataRequest),
    yield takeLatest(FarmTypes.REQUEST_SOIL_DATA_SUCCESS, handleUpdateLocation),
    yield takeLatest(FarmTypes.FETCH_PARCEL_CLU, handleFetchParcelCLUs),
    yield takeLatest(FarmTypes.UPDATE_FARM_ADDRESS, handleUpdateFarmAddress),
    yield takeLatest(FarmTypes.CREATE_FARM, handleCreateFarm),
    yield takeLatest(FarmTypes.UPDATE_FARM, handleUpdateFarm),
    yield takeLatest(FarmTypes.FETCH_FARM_OVERVIEW, handleFetchFarmOverview),
  ];
}
